// MaintenanceSystem — passive court repair by maintenance staff,
// building decay and staff happiness, processed weekly.

import { EventBus, GameEvents } from '../utils/EventBus'
import { useGameStore } from '@/store/gameStore'
import { getMaintenanceEffects, getMedicalEffects } from './staffEffects'

const BUILDING_DECAY_PER_WEEK = 1
const HAPPINESS_DEBT_PENALTY = 10
const HAPPINESS_WEEKLY_RECOVERY = 2
export const REPAIR_COST_PER_POINT = 8
export const BUILDING_REPAIR_COST_PER_POINT = 6

/** Manual repair: restore a court to 100% for a cost proportional to damage */
export function repairCourt(courtId: string): boolean {
  const store = useGameStore.getState()
  const court = store.courts.find(c => c.id === courtId)
  if (!court) return false

  const damage = 100 - court.condition
  if (damage <= 0) return false

  const cost = Math.round(damage * REPAIR_COST_PER_POINT * (court.type === 'indoor' ? 1.5 : 1))
  if (store.money < cost) return false

  store.subtractMoney(cost, `Reparacion de pista`)
  store.updateCourt(courtId, { condition: 100 })
  return true
}

export function getRepairCost(condition: number, type: 'outdoor' | 'indoor'): number {
  return Math.round((100 - condition) * REPAIR_COST_PER_POINT * (type === 'indoor' ? 1.5 : 1))
}

export function getBuildingRepairCost(condition: number): number {
  return Math.round((100 - condition) * BUILDING_REPAIR_COST_PER_POINT)
}

/** Manual repair: restore a building to 100% for a cost proportional to damage */
export function repairBuilding(buildingId: string): boolean {
  const store = useGameStore.getState()
  const building = store.buildings.find(b => b.id === buildingId)
  if (!building) return false

  const damage = 100 - (building.condition ?? 100)
  if (damage <= 0) return false

  const cost = getBuildingRepairCost(building.condition ?? 100)
  if (store.money < cost) return false

  store.subtractMoney(cost, `Reparacion de edificio`)
  store.updateBuilding(buildingId, { condition: 100 })
  return true
}

export class MaintenanceSystem {
  private handler = () => {
    this.passiveRepair()
    this.decayBuildings()
    this.updateHappiness()
  }

  constructor() {
    EventBus.on(GameEvents.WEEK_CHANGED, this.handler)
  }

  destroy() {
    EventBus.off(GameEvents.WEEK_CHANGED, this.handler)
  }

  /** The best maintenance worker restores skill/20 condition per structure weekly */
  private passiveRepair() {
    const store = useGameStore.getState()
    const { repairPerWeek: repairPerStructure } = getMaintenanceEffects(store.staff)
    if (repairPerStructure <= 0) return

    for (const court of store.courts) {
      if (court.condition >= 100) continue
      store.updateCourt(court.id, {
        condition: Math.min(100, Math.round((court.condition + repairPerStructure) * 10) / 10),
      })
    }

    // Maintenance staff also keep the buildings from crumbling
    for (const building of store.buildings) {
      const current = building.condition ?? 100
      if (current >= 100) continue
      store.updateBuilding(building.id, {
        condition: Math.min(100, Math.round((current + repairPerStructure) * 10) / 10),
      })
    }
  }

  private decayBuildings() {
    const store = useGameStore.getState()
    for (const building of store.buildings) {
      const current = building.condition ?? 100
      if (current <= 0) continue
      store.updateBuilding(building.id, {
        condition: Math.max(0, current - BUILDING_DECAY_PER_WEEK),
      })
    }
  }

  /** Weeks in the red hurt morale; healthy finances slowly restore it.
   *  A psychologist adds a passive weekly boost for everyone. */
  private updateHappiness() {
    const store = useGameStore.getState()
    if (store.staff.length === 0) return

    const { moralePassive } = getMedicalEffects(store.staff)
    const delta = (store.money < 0 ? -HAPPINESS_DEBT_PENALTY : HAPPINESS_WEEKLY_RECOVERY) + moralePassive
    for (const member of store.staff) {
      const happiness = Math.max(0, Math.min(100, member.happiness + delta))
      if (happiness !== member.happiness) {
        store.updateStaff(member.id, { happiness })
      }
    }
  }
}
